/**
 * API wiring: the token store, `configureApi` for the generated client (@oex/contracts), and the
 * QueryClient. Query keys are namespaced `[module, orgId, …]` by the feature `api.ts` wrappers
 * (docs/09 "Data access"); switching org clears the cache.
 */
import { configureApi, isApiError, type Problem } from '@oex/contracts';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

export const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000';

const KEYS = {
  token: 'oex.token',
  refresh: 'oex.refresh',
  staffToken: 'oex.token.staff',
  staffRefresh: 'oex.refresh.staff',
  org: 'oex.org',
} as const;

function read(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // storage unavailable (private mode): the session lives in memory only
  }
}

/** Bearer token (AuthKit access token, dev token, or an `oex_imp_…` impersonation token). */
export const tokenStore = {
  get: () => read(KEYS.token),
  set: (token: string | null) => write(KEYS.token, token),
  /** AuthKit refresh token, used to renew an expired access token (null for dev/impersonation). */
  getRefresh: () => read(KEYS.refresh),
  setRefresh: (token: string | null) => write(KEYS.refresh, token),
  /** The staff member's own token (and refresh token), parked while they impersonate someone. */
  getStaff: () => read(KEYS.staffToken),
  setStaff: (token: string | null) => write(KEYS.staffToken, token),
  getStaffRefresh: () => read(KEYS.staffRefresh),
  setStaffRefresh: (token: string | null) => write(KEYS.staffRefresh, token),
};

export const orgStore = {
  get: () => read(KEYS.org),
  set: (orgId: string | null) => write(KEYS.org, orgId),
};

export type ProblemHandlers = {
  /** 402 ENTITLEMENT_MISSING from any mutation: the shell opens the upsell dialog. */
  onEntitlementMissing(problem: Problem): void;
};

export function createQueryClient(handlers: ProblemHandlers): QueryClient {
  const onError = (error: unknown) => {
    if (isApiError(error) && error.status === 402) handlers.onEntitlementMissing(error.problem);
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (count, error) => !(isApiError(error) && error.status < 500) && count < 2,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Renew an expired access token from the stored refresh token (single-flight so a burst of 401s
 * triggers one refresh). On success the new token/refresh token are written to the store; on
 * failure the session is cleared and the caller's 401 falls through to onUnauthorized (sign-out).
 * Impersonation tokens have no refresh token, so this is a no-op while impersonating.
 */
let refreshing: Promise<boolean> | null = null;
function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return Promise.resolve(false);
  refreshing = (async () => {
    try {
      const res = await fetch(`${API_URL}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        tokenStore.set(null);
        tokenStore.setRefresh(null);
        return false;
      }
      const data = (await res.json()) as { token: string; refreshToken: string | null };
      tokenStore.set(data.token);
      tokenStore.setRefresh(data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export function setupApi(options: { getOrgId(): string | null; onUnauthorized(): void }): void {
  configureApi({
    baseUrl: API_URL,
    getToken: tokenStore.get,
    getOrgId: options.getOrgId,
    refresh: refreshSession,
    onUnauthorized: options.onUnauthorized,
  });
}
